import { Fragment, useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import Loader from '../shared/Loader';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from '../shared/ThemeToggle';
import {
  HomeIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  XMarkIcon,
  Bars3Icon,
  ChevronDownIcon,
  UsersIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import MobileNavigation from './MobileNavigation';
import { useSwipeable } from 'react-swipeable';
import debounce from 'lodash/debounce';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  {
    name: 'Contacts',
    href: '/contacts',
    icon: UsersIcon,
    children: [
      { name: 'Customers', href: '/contacts/customers' },
      { name: 'Suppliers', href: '/contacts/suppliers' }
    ]
  },
  {
    name: 'Purchase Management',
    href: '/purchases',
    icon: ShoppingCartIcon
  },
  {
    name: 'Sales Management',
    href: '/sales',
    icon: CurrencyDollarIcon
  },
  {
    name: 'Transportation',
    href: '/transportation',
    icon: TruckIcon
  },
  {
    name: 'Box Management',
    href: '/crates',
    icon: ArchiveBoxIcon
  },
];

const NavItem = ({ item, onNavigate, isCollapsed, onExpandSidebar }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  if (item.children) {
    return (
      <div className="py-1">
        <button
          onClick={() => {
            if (isCollapsed) {
              onExpandSidebar();
            }
            setIsOpen(!isOpen);
          }}
          title={isCollapsed ? item.name : ''}
          className={`w-full flex items-center ${!isCollapsed ? 'justify-between' : 'justify-center'} px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#333A48] rounded-md transition-colors duration-150 ease-in-out`}
        >
          <div className={`flex items-center ${!isCollapsed ? 'space-x-3' : ''}`}>
            {item.icon && <item.icon className="h-5 w-5" />}
            {!isCollapsed && <span>{item.name}</span>}
          </div>
          {!isCollapsed && (
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          )}
        </button>
        {!isCollapsed && isOpen && (
          <div className="mt-2 ml-4 space-y-1">
            {item.children.map((child) => (
              <Link
                key={child.name}
                to={child.href}
                onClick={onNavigate}
                className={`flex items-center space-x-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors duration-150 ease-in-out ${
                  location.pathname === child.href
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#333A48] hover:text-gray-900 dark:hover:text-gray-300'
                }`}
              >
                <span>{child.name}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      key={item.name}
      to={item.href}
      onClick={onNavigate}
      title={isCollapsed ? item.name : ''}
      className={`flex items-center ${!isCollapsed ? 'space-x-3' : 'justify-center'} px-4 py-3 text-sm font-medium rounded-md transition-colors duration-150 ease-in-out ${
        location.pathname === item.href
          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#333A48]'
      }`}
    >
      {item.icon && (
        <item.icon 
          className={`h-5 w-5 ${
            location.pathname === item.href 
              ? 'text-primary-600 dark:text-primary-400' 
              : ''
          }`}
        />
      )}
      {!isCollapsed && <span>{item.name}</span>}
    </Link>
  );
};

const SidebarContent = ({ onNavigate, isCollapsed, onToggleCollapse }) => (
  <div className="flex h-full flex-col">
    <div className="flex h-16 items-center px-6 border-b border-gray-200 dark:border-gray-800 transition-colors duration-300 justify-between">
      <Link to="/" onClick={onNavigate} className="flex items-center space-x-3">
        <div className="bg-primary-600 rounded p-1.5">
          <HomeIcon className="h-6 w-6 text-white" />
        </div>
        {!isCollapsed && <span className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-300">GeoSupply</span>}
      </Link>
      <button
        onClick={onToggleCollapse}
        className="hidden lg:block text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
      >
        <ChevronLeftIcon className={`h-5 w-5 transform transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`} />
      </button>
    </div>

    <div className="flex-1 px-3 overflow-y-auto sidebar-scroll">
      <p className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400/80 transition-colors duration-300 ${isCollapsed ? 'text-center' : ''}`}>
        {!isCollapsed && 'Menu'}
      </p>
      <nav className="mt-3 space-y-2 pb-4">
        {navigation.map((item) => (
          <NavItem 
            key={item.name} 
            item={item} 
            onNavigate={onNavigate} 
            isCollapsed={isCollapsed}
            onExpandSidebar={() => onToggleCollapse(false)}
          />
        ))}
      </nav>
    </div>
  </div>
);

const ProfileMenu = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center space-x-3 focus:outline-none">
        <img
          src="assets/avatar.jpg"
          alt="Profile"
          className="h-8 w-8 lg:h-10 lg:w-10 rounded-full ring-2 ring-gray-200 dark:ring-gray-700 transition-colors duration-200"
        />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-[#2E3B4E] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                  {currentUser?.displayName || currentUser?.email}
                </div>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/profile"
                  className={`${active ? 'bg-gray-100 dark:bg-gray-700' : ''
                    } block px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                >
                  Profile
                </Link>
              )}
            </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/settings/business"
                    className={`${active ? 'bg-gray-100 dark:bg-gray-700' : ''
                      } block px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                  >
                    Business Settings
                  </Link>
                )}
              </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={`${active ? 'bg-gray-100 dark:bg-gray-700' : ''
                    } block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200`}
                >
                  Sign out
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default function Layout({ children }) {
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true'
  );
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    localStorage.getItem('sidebarCollapsed') === 'true'
  );

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);

  const handleNavigate = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const debouncedSetSidebarOpen = debounce(setSidebarOpen, 100);

  const swipeHandlers = useSwipeable({
    onSwipedRight: () => {
      if (!sidebarOpen && window.innerWidth < 1024) {
        debouncedSetSidebarOpen(true);
      }
    },
    onSwipedLeft: () => {
      if (sidebarOpen && window.innerWidth < 1024) {
        debouncedSetSidebarOpen(false);
      }
    },
    trackMouse: false,
    trackTouch: true,
    delta: 50,
    preventScrollOnSwipe: true,
    swipeDuration: 500,
    enabled: window.innerWidth < 1024,
  });

  const handleToggleCollapse = (forcedState) => {
    setIsCollapsed(typeof forcedState === 'boolean' ? forcedState : !isCollapsed);
  };

  return (
    <ThemeProvider theme={{ mode: darkMode ? 'dark' : 'light' }}>
      {loading && <Loader />}
      <div
        {...swipeHandlers}
        className={`min-h-screen bg-gray-100 dark:bg-[#1D2430] transition-colors duration-300 ${loading ? 'hidden' : ''}`}
      >
        {/* Mobile sidebar - show when "More" is clicked */}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
            {...swipeHandlers}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <div className="absolute right-0 top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                  <div className="flex grow flex-col bg-white dark:bg-[#2E3B4E] py-6 transition-colors duration-300">
                    <SidebarContent onNavigate={handleNavigate} />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Desktop sidebar */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex ${isCollapsed ? 'lg:w-20' : 'lg:w-72'} transition-all duration-300`}>
          <div className="flex grow flex-col bg-white dark:bg-[#2E3B4E] transition-colors duration-300">
            <SidebarContent 
              onNavigate={handleNavigate} 
              isCollapsed={isCollapsed} 
              onToggleCollapse={handleToggleCollapse}
            />
          </div>
        </div>

        {/* Main content - adjust padding based on sidebar state */}
        <div className={`${isCollapsed ? 'lg:pl-20' : 'lg:pl-72'} transition-all duration-300`}>
          {/* Header */}
          <header className="sticky top-0 z-40 flex h-16 bg-white dark:bg-[#2E3B4E] border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-1 items-center justify-between px-4 sm:px-6 lg:px-8">
              {/* Mobile menu button - hide on desktop */}
              <button
                type="button"
                className="lg:hidden -m-2.5 p-2.5 text-gray-700 dark:text-gray-400"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>

              {/* Search */}
              <div className="flex items-center flex-1 max-w-xl ml-4 lg:ml-0">

              </div>

              {/* Right side icons */}
              <div className="flex items-center space-x-4 ml-auto">
                <ThemeToggle />
                <ProfileMenu />
              </div>
            </div>
          </header>

          {/* Page content - adjust padding for mobile */}
          <main className="py-6 px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
            {children}
          </main>

          {/* Mobile Navigation */}
          <MobileNavigation onMoreClick={() => setSidebarOpen(true)} />
        </div>
      </div>
    </ThemeProvider>
  );
}